import React, { useState, useRef, useEffect } from 'react';
import * as tf from '@tensorflow/tfjs';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import './RecommendationDemo.css';
import { defaultPreferences } from './settings/defaultPreferences';
const DELAY_MS = 50;  // Animation delay in milliseconds
const EMBEDDING_DIM = 3;  // Changed from 2 to 3
const INITIAL_RANGE = 1;
const RANDOM_SEED = 2;   // Add random seed

// Learning parameters
const MARGIN = 3;                 // Margin for Euclidean distance
const LEARNING_RATE = 0.01;       // Keep small learning rate for Adam

// Adam optimizer parameters
const BETA1 = 0.9;
const BETA2 = 0.999;
const EPSILON = 1e-8;

const INIT_MAX_ITERATIONS = 200;

const PREFERENCE_TYPES = {
  POSITIVE: 'positive',
  NEGATIVE: 'negative'
};

const computeEuclideanDistance = (emb1, emb2) => {
  const dx = emb1[0] - emb2[0];
  const dy = emb1[1] - emb2[1];
  const dz = emb1[2] - emb2[2];
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
};

const computeTensorDistance = (tensor1, tensor2) => {
  return tf.tidy(() => {
    return tf.sqrt(tf.sum(tf.square(tf.sub(tensor1, tensor2))));
  });
};

const CAMERA_POSITION = [3, 3, 3];  // Start further back
const CAMERA_FOV = 50;  // Narrower FOV for better depth perception
const BACKGROUND_COLOR = 0xfcfcfc;
const MIN_VISUALIZATION_SIZE = 2.0;
const PADDING_FACTOR = 1.6;  // Increased padding
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4.0;

// Add these constants at the top
const FRAME_RATE = 60; // Target frame rate
const FRAME_INTERVAL = 1000 / FRAME_RATE; // Time between frames in milliseconds

const RecommendationDemo = () => {
  const [persons] = useState([
    { id: 1, name: 'Alice', emoji: '👧🏽', preferences: [] },
    { id: 2, name: 'Bob', emoji: '🧛‍♂️', preferences: [] },
    { id: 3, name: 'Carol', emoji: '👩‍🎓', preferences: [] },
    { id: 4, name: 'David', emoji: '👨‍💻', preferences: [] },
  ]);

  // sports
  const [products] = useState([
    { id: 1, name: 'Cycling', emoji: '🚴‍♂️' },
    { id: 2, name: 'Skiing', emoji: '🎿' },
    { id: 3, name: 'Martial Arts', emoji: '🤼' },
    { id: 4, name: 'Basketball', emoji: '🏀' },
    { id: 5, name: 'Golf', emoji: '🏌️‍♂️' },
    { id: 6, name: 'Swimming', emoji: '🏊‍♂️' },
  ]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [embeddings, setEmbeddings] = useState(null);
  const [iterationCount, setIterationCount] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [currentMaxIterations, setCurrentMaxIterations] = useState(INIT_MAX_ITERATIONS);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const timeoutIdRef = useRef(null);

  // Add a new state for tracking drag state
  const [isDragging, setIsDragging] = useState(false);

  // Add dragOver state to track which person card is being dragged over
  const [dragOverId, setDragOverId] = useState(null);

  // Add new refs for Three.js
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const entityMeshesRef = useRef(new Map());

  // Add new ref for controls
  const controlsRef = useRef(null);

  const handleProductClick = (product) => {
    setSelectedProduct(selectedProduct?.id === product.id ? null : product);
  };

  const handlePersonClick = (person, prefType) => {
    if (!selectedProduct) return;
    
    const alreadyAssigned = preferences.some(
      pref => pref.personId === person.id && pref.productId === selectedProduct.id
    );

    if (!alreadyAssigned) {
      setPreferences(prev => [...prev, {
        personId: person.id,
        productId: selectedProduct.id,
        person: person.name,
        product: selectedProduct.name,
        type: prefType
      }]);
    }
    setSelectedProduct(null);
  };

  const handleDragStart = (e, product) => {
    e.dataTransfer.setData('product', JSON.stringify(product));
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleDrop = (e, person, prefType) => {
    e.preventDefault();
    const product = JSON.parse(e.dataTransfer.getData('product'));
    
    const alreadyAssigned = preferences.some(
      pref => pref.personId === person.id && pref.productId === product.id
    );

    if (!alreadyAssigned) {
      setPreferences(prev => [...prev, {
        personId: person.id,
        productId: product.id,
        person: person.name,
        product: product.name,
        type: prefType
      }]);
    }
  };

  const handleDragOver = (e, personId) => {
    e.preventDefault();
    setDragOverId(personId);
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const removePreference = (personId, productId) => {
    setPreferences(prev => 
      prev.filter(pref => !(pref.personId === personId && pref.productId === productId))
    );
  };

  // Add this helper function for better initialization
  const generateSphericalEmbeddings = (numPoints, radius = 1) => {
    const points = [];
    const phi = Math.PI * (3 - Math.sqrt(5)); // golden angle in radians

    for (let i = 0; i < numPoints; i++) {
      const y = 1 - (i / (numPoints - 1)) * 2;  // y goes from 1 to -1
      const radius_at_y = Math.sqrt(1 - y * y);  // radius at y
      
      const theta = phi * i;  // golden angle increment

      const x = Math.cos(theta) * radius_at_y;
      const z = Math.sin(theta) * radius_at_y;

      points.push([x * radius, y * radius, z * radius]);
    }

    return points;
  };

  // Update initializeEmbeddings function
  const initializeEmbeddings = () => {
    const allEntities = [...persons, ...products];
    
    // Generate points on a sphere for better distribution
    const sphericalPoints = generateSphericalEmbeddings(allEntities.length, INITIAL_RANGE);
    
    // Add some randomness to the spherical distribution
    const embeddingsTensor = tf.tidy(() => {
      const baseEmbeddings = tf.tensor2d(sphericalPoints);
      const noise = tf.randomUniform(
        [allEntities.length, EMBEDDING_DIM],
        -INITIAL_RANGE * 0.1,
        INITIAL_RANGE * 0.1,
        null,
        RANDOM_SEED
      );
      return tf.add(baseEmbeddings, noise);
    });

    setEmbeddings(embeddingsTensor);
    return embeddingsTensor;
  };

  const calculateRecommendations = (embeddingsArray) => {
    const recommendations = [];
    const numPersons = persons.length;
    
    persons.forEach((person, personIdx) => {
      const personEmb = embeddingsArray[personIdx];
      if (!personEmb) return;

      const availableProducts = products.filter(product => 
        !preferences.some(pref => 
          pref.personId === person.id && pref.productId === product.id
        )
      );

      if (availableProducts.length === 0) return;
      
      // Calculate distances
      const distances = availableProducts.map(product => {
        const productIdx = products.findIndex(p => p.id === product.id);
        const productEmb = embeddingsArray[numPersons + productIdx];
        const distance = computeEuclideanDistance(personEmb, productEmb);
        return { product, distance };
      });
      
      // Sort by distance (lower is better)
      distances.sort((a, b) => a.distance - b.distance);
      
      if (distances.length > 0) {
        const closest = distances[0];
        const farthest = distances[distances.length - 1];
        
        recommendations.push({
          personId: person.id,
          productId: closest.product.id,
          product: closest.product.name,
          type: PREFERENCE_TYPES.POSITIVE,
          isRecommendation: true,
          distance: closest.distance
        });
        
        if (closest.product.id !== farthest.product.id) {
          recommendations.push({
            personId: person.id,
            productId: farthest.product.id,
            product: farthest.product.name,
            type: PREFERENCE_TYPES.NEGATIVE,
            isRecommendation: true,
            distance: farthest.distance
          });
        }
      }
    });
    
    return recommendations;
  };

  // Update the generateTriplets function to return all triplets with weights
  const generateTriplets = (embeddingsVar) => {
    return tf.tidy(() => {
      const numPersons = persons.length;
      const triplets = [];
      
      persons.forEach((person, personIdx) => {
        const personPrefs = preferences.filter(pref => pref.personId === person.id);
        const positives = personPrefs.filter(pref => pref.type === PREFERENCE_TYPES.POSITIVE);
        const negatives = personPrefs.filter(pref => pref.type === PREFERENCE_TYPES.NEGATIVE);

        if (positives.length === 0 || negatives.length === 0) return;

        const anchorEmb = embeddingsVar.slice([personIdx, 0], [1, EMBEDDING_DIM]);
        
        // Generate all possible triplet combinations with weights
        positives.forEach(posPref => {
          const posIdx = products.findIndex(p => p.id === posPref.productId) + numPersons;
          const posEmb = embeddingsVar.slice([posIdx, 0], [1, EMBEDDING_DIM]);
          const posDistance = computeTensorDistance(anchorEmb, posEmb);

          negatives.forEach(negPref => {
            const negIdx = products.findIndex(p => p.id === negPref.productId) + numPersons;
            const negEmb = embeddingsVar.slice([negIdx, 0], [1, EMBEDDING_DIM]);
            const negDistance = computeTensorDistance(anchorEmb, negEmb);
            
            // Calculate violation of margin and use it as weight
            const violation = tf.sub(posDistance, negDistance).add(tf.scalar(MARGIN));
            const hardness = tf.sigmoid(violation).dataSync()[0];
            
            triplets.push({
              anchorIdx: personIdx,
              positiveIdx: posIdx,
              negativeIdx: negIdx,
              weight: hardness,
            });
          });
        });
      });

      return triplets;
    });
  };

  // Update the optimizeStep function to use weighted loss
  const optimizeStep = async (currentEmbeddings) => {
    return new Promise((resolve) => {
      // Use requestAnimationFrame to ensure we're not blocking the main thread
      requestAnimationFrame(() => {
        const result = tf.tidy(() => {
          const optimizer = tf.train.adam(LEARNING_RATE, BETA1, BETA2, EPSILON);
          const embeddingsVar = tf.variable(currentEmbeddings);
          
          const triplets = generateTriplets(embeddingsVar);
          if (triplets.length === 0) {
            return { embeddings: currentEmbeddings.clone(), loss: 0 };
          }

          let loss = null;
          optimizer.minimize(() => {
            const losses = triplets.map(triplet => {
              const { anchorIdx, positiveIdx, negativeIdx, weight } = triplet;
              
              const anchor = embeddingsVar.slice([anchorIdx, 0], [1, EMBEDDING_DIM]);
              const positive = embeddingsVar.slice([positiveIdx, 0], [1, EMBEDDING_DIM]);
              const negative = embeddingsVar.slice([negativeIdx, 0], [1, EMBEDDING_DIM]);

              const posDistance = computeTensorDistance(anchor, positive);
              const negDistance = computeTensorDistance(anchor, negative);

              return tf.maximum(
                tf.mul(
                  tf.add(tf.sub(posDistance, negDistance), tf.scalar(MARGIN)),
                  tf.scalar(weight)
                ),
                tf.scalar(0)
              );
            });

            loss = tf.mean(tf.stack(losses));
            return loss;
          }, true, [embeddingsVar]);

          const lossValue = loss.dataSync()[0];
          const updatedEmbeddings = embeddingsVar.clone();
          
          return {
            embeddings: updatedEmbeddings,
            loss: lossValue
          };
        });

        resolve(result);
      });
    });
  };

  // Update initThreeJS function
  const initThreeJS = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // Reset canvas
    canvas.width = container.clientWidth;
    canvas.height = Math.min(Math.max(container.clientWidth * 0.5, 250), 400);

    // Setup scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(BACKGROUND_COLOR);
    sceneRef.current = scene;

    // Setup camera
    const camera = new THREE.PerspectiveCamera(
      CAMERA_FOV,
      canvas.width / canvas.height,
      0.1,
      1000
    );
    camera.position.set(...CAMERA_POSITION);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Setup renderer
    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
    });
    renderer.setSize(canvas.width, canvas.height, false);  // false to prevent setting canvas style
    renderer.setPixelRatio(window.devicePixelRatio);
    rendererRef.current = renderer;

    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    // Add directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.4);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // Add axes helper for debugging
    const axesHelper = new THREE.AxesHelper(0.5);
    scene.add(axesHelper);

    // Add OrbitControls with modified settings
    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = MIN_VISUALIZATION_SIZE * MIN_ZOOM;
    controls.maxDistance = MIN_VISUALIZATION_SIZE * MAX_ZOOM;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 1.0;
    controls.hasBeenMoved = false;
    
    // Track any user interaction with controls
    controls.addEventListener('start', () => {
      controls.hasBeenMoved = true;
      controls.autoRotate = false; // Stop auto-rotation on user interaction
    });
    
    controls.update();
    controlsRef.current = controls;

    // Initial render
    renderer.render(scene, camera);
  };

  // Add this helper function to compute bounds and center
  const computeVisualizationBounds = (embeddingsArray) => {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;
    
    embeddingsArray.forEach(emb => {
      minX = Math.min(minX, emb[0]);
      maxX = Math.max(maxX, emb[0]);
      minY = Math.min(minY, emb[1]);
      maxY = Math.max(maxY, emb[1]);
      minZ = Math.min(minZ, emb[2]);
      maxZ = Math.max(maxZ, emb[2]);
    });

    // Calculate center
    const center = new THREE.Vector3(
      (minX + maxX) / 2,
      (minY + maxY) / 2,
      (minZ + maxZ) / 2
    );

    // Calculate size and ensure minimum size
    const size = Math.max(
      maxX - minX,
      maxY - minY,
      maxZ - minZ,
      MIN_VISUALIZATION_SIZE
    ) * PADDING_FACTOR;

    return { center, size };
  };

  // Add this helper function for smooth camera transitions
  const smoothCameraTransition = (camera, controls, targetCenter, targetSize, duration = 1000) => {
    const startTarget = controls.target.clone();
    const startTime = Date.now();
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easing function for smooth transition
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      // Interpolate target
      controls.target.lerpVectors(startTarget, targetCenter, easeProgress);
      
      // Update camera position while maintaining relative orientation
      const currentDirection = camera.position.clone().sub(controls.target).normalize();
      const targetDistance = targetSize / Math.tan((camera.fov * Math.PI / 180) / 1.1);
      camera.position.copy(controls.target.clone().add(currentDirection.multiplyScalar(targetDistance)));
      
      controls.update();
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  };

  // Update the fitCameraToEntities function to use smooth transition
  const fitCameraToEntities = (camera, controls, center, size, smooth = true) => {
    if (smooth) {
      smoothCameraTransition(camera, controls, center, size);
    } else {
      const fov = camera.fov * Math.PI / 180;
      const fitHeightDistance = size / (2 * Math.tan(fov / 2));
      const fitWidthDistance = size / (2 * Math.tan(fov / 2 * camera.aspect));
      const idealDistance = Math.max(fitHeightDistance, fitWidthDistance);

      const direction = camera.position.clone().sub(controls.target).normalize();
      camera.position.copy(center.clone().add(direction.multiplyScalar(idealDistance)));
      controls.target.copy(center);
      
      camera.near = size / 100;
      camera.far = size * 100;
      camera.updateProjectionMatrix();
      
      controls.maxDistance = idealDistance * 2;
      controls.minDistance = idealDistance * 0.5;
      
      controls.update();
    }
  };

  // Update the draw3D function
  const draw3D = (embeddings) => {
    if (!embeddings || embeddings.isDisposed) {
      console.error('Invalid embeddings tensor');
      return;
    }

    try {
      const embeddingsArray = embeddings.arraySync();
      const scene = sceneRef.current;
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const controls = controlsRef.current;
      
      if (!scene || !renderer || !camera) {
        initThreeJS();
        return;
      }

      // Clear previous meshes
      entityMeshesRef.current.forEach(mesh => {
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (mesh.material.map) mesh.material.map.dispose();
          mesh.material.dispose();
        }
        scene.remove(mesh);
      });
      entityMeshesRef.current.clear();

      // Compute visualization bounds
      const { center, size } = computeVisualizationBounds(embeddingsArray);
      
      // Update controls target and fit camera if not manipulated
      if (controls) {
        if (!controls.hasBeenMoved) {
          fitCameraToEntities(camera, controls, center, size);
        } else {
          controls.target.copy(center);
          controls.minDistance = size * MIN_ZOOM;
          controls.maxDistance = size * MAX_ZOOM;
          controls.update();
        }
      }

      // Create sprites for entities
      const allEntities = [
        ...persons.map((p, i) => ({ ...p, idx: i, type: 'person' })),
        ...products.map((p, i) => ({ ...p, idx: i + persons.length, type: 'product' }))
      ];

      // Scale factor for sprites based on visualization size
      const spriteScale = size * 0.1;

      // Create text sprites for entities
      allEntities.forEach(entity => {
        const emb = embeddingsArray[entity.idx];
        
        // Create sprite
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 128;
        canvas.height = 128;
        
        context.font = '96px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(entity.emoji, canvas.width/2, canvas.height/2);
        
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        const spriteMaterial = new THREE.SpriteMaterial({ 
          map: texture,
          transparent: true,
        });
        const sprite = new THREE.Sprite(spriteMaterial);
        
        sprite.position.set(emb[0], emb[1], emb[2]);
        sprite.scale.set(spriteScale, spriteScale, spriteScale);
        
        scene.add(sprite);
        entityMeshesRef.current.set(entity.idx, sprite);
      });

      // Draw preference lines with scaled width
      const lineWidth = size * 0.005;
      preferences.forEach(pref => {
        const personIdx = persons.findIndex(p => p.id === pref.personId);
        const productIdx = products.findIndex(p => p.id === pref.productId) + persons.length;
        
        const personEmb = embeddingsArray[personIdx];
        const productEmb = embeddingsArray[productIdx];
        
        const points = [
          new THREE.Vector3(...personEmb),
          new THREE.Vector3(...productEmb)
        ];
        
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({
          color: pref.type === PREFERENCE_TYPES.POSITIVE ? 0x00ff00 : 0xff0000, // rgba(76, 175, 80, 0.2) : rgba(244, 67, 54, 0.3)
          opacity: 0.6,
          transparent: true,
          linewidth: lineWidth
        });
        
        const line = new THREE.Line(geometry, material);
        scene.add(line);
        entityMeshesRef.current.set(`line-${personIdx}-${productIdx}`, line);
      });

      // Update animation
      if (!rendererRef.current.animation) {
        let lastFrameTime = Date.now();

        const animate = () => {
          rendererRef.current.animation = requestAnimationFrame(animate);
          
          const currentTime = Date.now();
          const deltaTime = currentTime - lastFrameTime;

          // Only render if enough time has passed for next frame
          if (deltaTime >= FRAME_INTERVAL) {
            if (controls) {
              controls.update(); // This will handle auto-rotation if enabled
            }
            
            renderer.render(scene, camera);
            lastFrameTime = currentTime - (deltaTime % FRAME_INTERVAL);
          }
        };
        animate();
      }
    } catch (error) {
      console.error('Error in draw3D:', error);
      return;
    }
  };

  // Update the optimization loop effect
  useEffect(() => {
    let animationFrameId = null;
    let isAnimating = false;

    const runOptimization = async () => {
      if (!isRunning || !embeddings || iterationCount >= currentMaxIterations || isAnimating) {
        setIsRunning(false);
        
        // Start auto-rotation when optimization completes, but only if user hasn't moved controls
        if (iterationCount >= currentMaxIterations && controlsRef.current && !controlsRef.current.hasBeenMoved) {
          controlsRef.current.autoRotate = true;
          controlsRef.current.autoRotateSpeed = 1.0; // Adjust speed as needed (degrees per second)
        }
        
        return;
      }

      try {
        isAnimating = true;
        const result = await optimizeStep(embeddings);
        
        if (!isRunning) {
          isAnimating = false;
          return;
        }

        if (result.embeddings) {
          const oldEmbeddings = embeddings;
          const newEmbeddings = result.embeddings;
          
          // Update state
          setEmbeddings(newEmbeddings);
          setIterationCount(prev => prev + 1);
          
          // Draw first, then dispose
          draw3D(newEmbeddings);
          oldEmbeddings.dispose();

          // Wait for next frame before continuing
          animationFrameId = requestAnimationFrame(() => {
            isAnimating = false;
            // Schedule next optimization step
            timeoutIdRef.current = setTimeout(runOptimization, DELAY_MS);
          });
        }
      } catch (error) {
        console.error('Optimization error:', error);
        isAnimating = false;
        stopOptimization();
      }
    };

    if (isRunning && isOptimizing) {
      runOptimization();
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      isAnimating = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, isOptimizing, embeddings, iterationCount, currentMaxIterations]);

  // Update startOptimization function
  const startOptimization = () => {
    setIsOptimizing(true);
    setIsRunning(true);
    
    if (iterationCount >= currentMaxIterations) {
      setCurrentMaxIterations(prev => prev + INIT_MAX_ITERATIONS);
    }

    // Initialize if needed
    if (!embeddings || embeddings.isDisposed) {
      const initialEmbeddings = initializeEmbeddings();
      cleanupThreeJS();
      initThreeJS();
      draw3D(initialEmbeddings);
    } else {
      draw3D(embeddings);
    }
  };

  // Update cleanupThreeJS to be more thorough
  const cleanupThreeJS = () => {
    // Cancel any ongoing animation
    if (rendererRef.current?.animation) {
      cancelAnimationFrame(rendererRef.current.animation);
      rendererRef.current.animation = null;
    }

    // Dispose of controls
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }

    // Clean up scene
    if (sceneRef.current) {
      while(sceneRef.current.children.length > 0) { 
        const object = sceneRef.current.children[0];
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => {
              if (material.map) material.map.dispose();
              material.dispose();
            });
          } else {
            if (object.material.map) object.material.map.dispose();
            object.material.dispose();
          }
        }
        sceneRef.current.remove(object);
      }
      sceneRef.current = null;
    }

    // Dispose of renderer
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }

    // Clear entity meshes
    entityMeshesRef.current.clear();

    // Reset camera
    cameraRef.current = null;
  };

  // Update stopOptimization to handle auto-rotation
  const stopOptimization = () => {
    setIsRunning(false);
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    
    // Stop auto-rotation when optimization is manually stopped
    if (controlsRef.current) {
      controlsRef.current.autoRotate = false;
    }
  };

  // Update the component unmount cleanup
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (embeddings) {
        embeddings.dispose();
      }
      cleanupThreeJS();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="recommendation-demo" ref={containerRef}>
      <div className="demo-container">
        <div className="persons-section">
          <h2>People</h2>
          <div className="persons-grid">
            {persons.map(person => (
              <div 
                key={person.id}
                className={`person-card ${dragOverId === person.id ? 'drag-over' : ''} ${selectedProduct ? 'has-selected-product' : ''}`}
                onDragOver={(e) => handleDragOver(e, person.id)}
                onDragLeave={handleDragLeave}
              >
                {(selectedProduct || (isDragging && dragOverId === person.id)) && (
                  <>
                    <div 
                      className="drop-overlay negative"
                      onDrop={(e) => {
                        handleDrop(e, person, PREFERENCE_TYPES.NEGATIVE);
                        setIsDragging(false);
                        setDragOverId(null);
                      }}
                      onDragOver={(e) => handleDragOver(e, person.id)}
                      onClick={() => handlePersonClick(person, PREFERENCE_TYPES.NEGATIVE)}
                    >
                      👎
                    </div>
                    <div 
                      className="drop-overlay positive"
                      onDrop={(e) => {
                        handleDrop(e, person, PREFERENCE_TYPES.POSITIVE);
                        setIsDragging(false);
                        setDragOverId(null);
                      }}
                      onDragOver={(e) => handleDragOver(e, person.id)}
                      onClick={() => handlePersonClick(person, PREFERENCE_TYPES.POSITIVE)}
                    >
                      👍
                    </div>
                  </>
                )}
                
                <div className="person-info">
                  <h3>
                    <span className="emoji">{person.emoji}</span>
                    <span className="name">{person.name}</span>
                  </h3>
                  <div className="preferences">
                    {preferences
                      .filter(pref => pref.personId === person.id)
                      .map((pref) => (
                        <div 
                          key={`${pref.personId}-${pref.productId}`} 
                          className={`preference ${isOptimizing ? 'locked' : ''} ${pref.type}`}
                          onClick={(e) => {
                            if (isOptimizing) return;
                            e.stopPropagation();
                            removePreference(pref.personId, pref.productId);
                          }}
                        >
                          {pref.product}
                          {!isOptimizing && (
                            <span className="remove-icon">✕</span>
                          )}
                        </div>
                      ))
                    }
                  </div>
                  {isOptimizing && embeddings && (
                    <div className="recommendations">
                      <div className="recommendations-list">
                        {calculateRecommendations(embeddings.arraySync())
                          .filter(rec => rec.personId === person.id)
                          .map((rec) => (
                            <div 
                              key={`rec-${rec.personId}-${rec.productId}`} 
                              className={`preference recommendation ${rec.type}`}
                            >
                              {rec.product}
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {!isOptimizing ? (
          <>
            <div className="products-section">
              <h2>Products</h2>
              <div className="products-grid">
                {products.map(product => (
                  <div
                    key={product.id}
                    className={`product-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, product)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handleProductClick(product)}
                  >
                    <span className="emoji">{product.emoji}</span>
                    <span className="name">{product.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="optimization-view">
            <canvas
              ref={canvasRef}
              className="embedding-canvas"
            />
          </div>
        )}
       
        
        <div className="rl-controls">
          <div className="rl-control-panel">
            {!isOptimizing ? (
              <button 
                className="rl-button rl-start-button" 
                onClick={startOptimization}
                disabled={preferences.length < 1}
              >
                Start Optimization
              </button>
            ) : (
              <>
                <button 
                  className={`rl-button ${isRunning ? 'rl-stop-button' : 'rl-continue-button'}`}
                  onClick={() => isRunning ? stopOptimization() : startOptimization()}
                >
                  {isRunning ? 'Stop' : 'Continue'} (Step: {iterationCount}/{currentMaxIterations})
                </button>
                <button 
                  className="rl-button"
                  onClick={() => {
                    stopOptimization();
                    setIsOptimizing(false);
                    setIterationCount(0);
                    setCurrentMaxIterations(INIT_MAX_ITERATIONS);
                    
                    // Clean up Three.js resources
                    cleanupThreeJS();
                    
                    // Clean up embeddings
                    if (embeddings) {
                      embeddings.dispose();
                      setEmbeddings(null);
                    }
                  }}
                >
                  Back to Input
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationDemo; 